import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { Upload, Download, FileText, Pencil, Trash2, ExternalLink, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import BulkDocumentUploadDialog from "@/components/BulkDocumentUploadDialog";

const DOC_TYPES = [
  { v: "id", label: "ID Proof" },
  { v: "certificate", label: "Certificate" },
  { v: "address_proof", label: "Address Proof" },
  { v: "contract", label: "Contract" },
  { v: "nda", label: "NDA" },
  { v: "msa", label: "MSA" },
  { v: "employment", label: "Employment agreement" },
  { v: "payslip", label: "Payslip" },
  { v: "other", label: "Other" },
];

const EMPTY_FORM = { doc_type: "id", title: "", user_id: "", drive_link: "", expiry_date: "" };

function expiryBadge(expiryDate) {
  if (!expiryDate) return null;
  const days = Math.ceil((new Date(expiryDate) - new Date()) / 86400000);
  if (days < 0) return { label: "Expired", tone: "bg-red-50 text-red-600" };
  if (days <= 15) return { label: `Expires in ${days}d`, tone: "bg-orange-50 text-orange-600" };
  return { label: `Valid to ${expiryDate}`, tone: "bg-slate-50 text-slate-500" };
}

export default function HR() {
  const { user } = useAuth();
  const can = usePermissions();
  const isAdmin = can("hr_docs_admin");
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  // Founders/Admins land on their own documents first ("myself first"), then
  // can switch to "All employees" or any specific person via the filter below.
  const [targetUser, setTargetUser] = useState("me");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const internalUsers = useMemo(() => users, [users]);

  const load = useCallback(async () => {
    const params = {};
    if (isAdmin && targetUser === "me") params.user_id = user.id;
    else if (isAdmin && targetUser !== "all") params.user_id = targetUser;
    const { data } = await api.get("/hr-docs", { params });
    setRows(data);
  }, [isAdmin, targetUser, user?.id]);

  useEffect(() => {
    load();
    if (isAdmin) {
      api.get("/users", { params: { kind: "internal" } }).then((r) => setUsers(r.data)).catch(() => {});
    }
  }, [load, isAdmin]);

  const upload = async () => {
    if (!form.drive_link.trim()) { toast.error("Paste a Google Drive link"); return; }
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    try {
      const fd = new FormData();
      fd.append("doc_type", form.doc_type);
      fd.append("title", form.title);
      if (form.user_id) fd.append("user_id", form.user_id);
      if (isAdmin && form.expiry_date) fd.append("expiry_date", form.expiry_date);
      fd.append("drive_link", form.drive_link.trim());
      await api.post("/hr-docs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document added · synced to Registry");
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const payload = { title: editing.title, doc_type: editing.doc_type };
      if (editing.kind === "link") payload.drive_link = editing.drive_link;
      if (isAdmin) payload.expiry_date = editing.expiry_date || null;
      await api.patch(`/hr-docs/${editing.id}`, payload);
      toast.success("Document updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? This removes it from the Registry too.`)) return;
    try {
      await api.delete(`/hr-docs/${r.id}`);
      toast.success("Document removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openDoc = async (r) => {
    if (r.kind === "link" && r.drive_link) {
      window.open(r.drive_link, "_blank", "noreferrer");
      return;
    }
    try {
      const resp = await api.get(`/files/${r.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.original_filename || r.title || "document";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const usersMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const visibleRows = useMemo(() => rows.filter((r) => !r.is_deleted), [rows]);

  const canEdit = (r) => isAdmin || r.user_id === user?.id || r.uploaded_by === user?.id;

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto fade-in" data-testid="hr-page">
      <PageHeader
        eyebrow="Documents"
        title="My documents"
        subtitle="Paste a Google Drive link. Every doc auto-syncs to the master Documents Registry."
        actions={
          <div className="flex items-center gap-2">
            {isAdmin && <BulkDocumentUploadDialog users={users} docTypes={DOC_TYPES} onDone={load} />}
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(EMPTY_FORM); }}>
            <DialogTrigger asChild><Button data-testid="upload-hr-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white"><Upload size={14} className="mr-1.5" />Add document</Button></DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle className="font-display">Add document</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <Label className="label-overline">Type</Label>
                  <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="label-overline">Title *</Label><Input className="rounded-sm mt-1" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="hr-title" /></div>
                {isAdmin && (
                  <div>
                    <Label className="label-overline">Employee</Label>
                    <Select value={form.user_id || "me"} onValueChange={(v) => setForm({ ...form, user_id: v === "me" ? "" : v })}>
                      <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Myself" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="me">Myself</SelectItem>
                        {internalUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {isAdmin && (
                  <div>
                    <Label className="label-overline">Expiry date (optional — for ID/contract renewal alerts)</Label>
                    <Input type="date" className="rounded-sm mt-1" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="hr-expiry-date" />
                  </div>
                )}

                {/* Google Drive link */}
                <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/60">
                  <Label className="label-overline">Google Drive link *</Label>
                  <Input className="rounded-sm mt-1.5" placeholder="https://drive.google.com/…" value={form.drive_link || ""} onChange={(e) => setForm({ ...form, drive_link: e.target.value })} data-testid="hr-link" />
                </div>
              </div>
              <DialogFooter><Button onClick={upload} data-testid="submit-hr-doc" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">Save</Button></DialogFooter>
            </DialogContent>
            </Dialog>
          </div>
        }
      />

      {isAdmin && (
        <div className="mb-4 flex items-center gap-3">
          <span className="label-overline">View</span>
          <Select value={targetUser} onValueChange={setTargetUser}>
            <SelectTrigger className="rounded-sm w-64"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All employees</SelectItem>
              <SelectItem value="me">My documents</SelectItem>
              {internalUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Card grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="hr-grid">
        {visibleRows.map((r) => (
          <div key={r.id} className="card-flat p-4" data-testid={`hr-card-${r.id}`}>
            <div className="flex items-start gap-3 mb-2">
              <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${r.kind === "link" ? "bg-blue-50 text-blue-600" : "bg-orange-50 text-[#F54800]"}`}>
                {r.kind === "link" ? <LinkIcon size={15} /> : <FileText size={15} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm text-slate-900 truncate" title={r.title}>{r.title}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">{r.doc_type} · {usersMap[r.user_id]?.name || (r.user_id === user?.id ? "You" : "—")}</div>
              </div>
            </div>
            {r.expiry_date && (() => {
              const badge = expiryBadge(r.expiry_date);
              return badge ? <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-sm mb-1 ${badge.tone}`} data-testid={`hr-expiry-badge-${r.id}`}>{badge.label}</span> : null;
            })()}
            <div className="flex items-center justify-between mt-3">
              <button onClick={() => openDoc(r)} className="text-[11px] text-[#F54800] hover:underline inline-flex items-center gap-1" data-testid={`hr-open-${r.id}`}>
                {r.kind === "link" ? <><ExternalLink size={10} />Open link</> : <><Download size={10} />Download</>}
              </button>
              {canEdit(r) && (
                <div className="inline-flex gap-1">
                  <button onClick={() => setEditing({ ...r })} className="p-1.5 text-slate-400 hover:text-slate-700" data-testid={`hr-edit-${r.id}`}><Pencil size={12} /></button>
                  <button onClick={() => remove(r)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`hr-delete-${r.id}`}><Trash2 size={12} /></button>
                </div>
              )}
            </div>
          </div>
        ))}
        {visibleRows.length === 0 && (
          <div className="col-span-full p-8 text-center text-sm text-slate-500 card-flat">No documents yet — click <b>Add document</b> to upload or paste a Drive link.</div>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-sm" data-testid="hr-edit-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit document</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 mt-2">
              <div><Label className="label-overline">Title</Label><Input className="rounded-sm mt-1" value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="hr-edit-title" /></div>
              <div>
                <Label className="label-overline">Type</Label>
                <Select value={editing.doc_type} onValueChange={(v) => setEditing({ ...editing, doc_type: v })}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d.v} value={d.v}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {editing.kind === "link" && (
                <div><Label className="label-overline">Drive link</Label><Input className="rounded-sm mt-1" value={editing.drive_link || ""} onChange={(e) => setEditing({ ...editing, drive_link: e.target.value })} data-testid="hr-edit-link" /></div>
              )}
              {editing.kind !== "link" && (
                <p className="text-[11px] text-slate-500 italic">To replace the underlying file, delete and re-add. Renaming / re-categorising works here.</p>
              )}
              {isAdmin && (
                <div>
                  <Label className="label-overline">Expiry date</Label>
                  <Input type="date" className="rounded-sm mt-1" value={editing.expiry_date || ""} onChange={(e) => setEditing({ ...editing, expiry_date: e.target.value })} data-testid="hr-edit-expiry-date" />
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" data-testid="hr-edit-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
